// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    API_URL_CURRENT_FRAGMENT,
    del,
    get,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { AddProductApiParameter } from "@insite/client-framework/Services/CartService";
import { CartLineModel, SectionOptionDto } from "@insite/client-framework/Types/ApiModels";

export interface CustomAddProductApiParameter extends AddProductApiParameter {
    properties?: { [key: string]: string };
}

const cartsUrl = "/api/v1/carts";
const cartUrl = "/api/tps/cart";

export async function CustomAddProductWithResult(
    parameter: CustomAddProductApiParameter,
): Promise<ServiceResult<CartLineModel>> {
    const cartLine = {
        productId: parameter.productId,
        qtyOrdered: parameter.qtyOrdered,
        unitOfMeasure: parameter.unitOfMeasure,
        notes: parameter.notes,
        sectionOptions: parameter.sectionOptions,
        // XNG-Change: send warehouseId
        properties: parameter.properties,
    } as CartLineModel;

    if (parameter.subscription) {
        cartLine.properties = {
            productSubscription: JSON.stringify(parameter.subscription),
        };
    }

    try {
        const cartLineModel = await post(`${cartsUrl}/${API_URL_CURRENT_FRAGMENT}/cartlines`, cartLine);
        return {
            successful: true,
            result: cartLineModel,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error.status === 403) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
                statusCode: 403,
            };
        }
        throw error;
    }
}

export interface RemoveCartLinesApiParameter extends ApiParameter {
    cartId: string;
    cartLinesIds: string[];
}

export function deleteCartLines(parameter: RemoveCartLinesApiParameter) {
    let cartlineurl = "";
    parameter.cartLinesIds.forEach(cartLineId => {
        cartlineurl += `cartLineIds=${cartLineId}&`;
    });
    cartlineurl = cartlineurl.slice(0, -1);
    return del(`${cartUrl}/${parameter.cartId}?${cartlineurl}`);
}
export interface GetShippingInformationApiParameter extends ApiParameter {
    warehouseId: string;
}
export interface SetShippingInformationApiParameter extends ApiParameter {
    WarehouseId: string;
    ShipViaId: string;
    ShippingMethod: string;
    TermsType?: string;
    BillToName?: string;
    Address1?: string;
    Address2?: string;
    City?: string;
    PostalCode?: string;
    AccountNumber?: string;
    State?: string;
    Notes: string;
}
export function getShippingInformation(parameter: GetShippingInformationApiParameter) {
    return get<any>(`${cartUrl}/current/GetCartShippingInformation`, parameter); // update the type
}

export function setShippingInformation(parameter: SetShippingInformationApiParameter) {
    return post(`${cartUrl}/current/AddCartShippingInformation`, parameter); // update the type
}

export function removeShippingInformation(parameter: string[]) {
    return del(`${cartUrl}/current/DeleteCartShippingInformation?warehouseIds=${parameter}`);
}
